import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { PolicyMarkdown, processPolicyContent } from '../../../../../PolicyPage/policyMarkdown';
import { policyVersionLine } from '../../../../../PolicyPage/policySanitize';

const Section = styled.section`
  ${tw`mb-8`}
  @media print {
    margin-bottom: 8px !important;
  }
`;

const SectionTitle = styled.h2`
  ${tw`text-xl font-bold mb-2`}
`;

const Text = styled.p`
  ${tw`text-sm leading-relaxed whitespace-pre-wrap`}
`;

const Line = styled.p`
  ${tw`text-sm text-text-secondary mb-4`}
`;

type FeeLine = { key?: string; label?: string; text?: string };

export type AgreementPayload = Record<string, any> | null | undefined;

export type AgreementVersion = {
  id?: string | null;
  title?: string | null;
  versionNumber?: number | null;
  effectiveDate?: string | null;
  contentMarkdown?: string | null;
  approximateDates?: boolean | null;
} | null;

function textOf(value: unknown): string {
  if (value == null || value === '') return '';
  return String(value);
}

function placeOf(place: any): string {
  if (!place) return '';
  return [place.name, place.address, place.city, place.state, place.zipCode]
    .filter(Boolean)
    .join(', ');
}

/** Summary, fee schedule and agreement text from one frozen record (spec 031). */
export const AgreementRecordView: React.FC<{
  payload: AgreementPayload;
  version: AgreementVersion;
  acceptances?: Array<{
    id: string;
    method?: string | null;
    acceptedAt?: string | null;
    version?: { title?: string | null; versionNumber?: number | null } | null;
  }>;
}> = ({ payload, version, acceptances = [] }) => {
  const parties = payload?.parties;
  const vehicle = payload?.vehicle;
  const trip = payload?.trip;
  const money = payload?.money;
  const distance = payload?.distance;
  const protection = payload?.protection;
  const lines = (payload?.rules?.feeSchedule?.lines ?? []) as FeeLine[];
  const versionLine = policyVersionLine({
    effectiveDate: version?.effectiveDate,
    versionNumber: version?.versionNumber,
    approximateDates: version?.approximateDates === true,
  });
  const guest = parties?.guest?.name || 'Guest';
  const host = parties?.host?.name || 'Host';
  const brand = parties?.brand?.displayName || parties?.brand?.brandSite || '';

  return (
    <div data-testid="agreement-record">
      {payload?.rebuiltAt ? (
        <Line data-testid="agreement-rebuilt">
          This record was rebuilt on {textOf(payload.rebuiltAt)} from the trip as it is now.
        </Line>
      ) : null}
      <Section id="trip-summary">
        <SectionTitle>
          {guest}&apos;s trip{brand ? ` with ${brand}` : ''}
        </SectionTitle>
        <Text>
          <strong>Primary driver:</strong> {guest}
        </Text>
        <Text>
          <strong>Host:</strong> {host}
        </Text>
        {vehicle ? (
          <Text>
            <strong>Vehicle:</strong>{' '}
            {[vehicle.year, vehicle.make, vehicle.model, vehicle.name].filter(Boolean).join(' ')}
          </Text>
        ) : null}
        {vehicle?.licensePlate ? (
          <Text>
            <strong>License plate:</strong> {vehicle.licensePlate}
          </Text>
        ) : null}
        {vehicle?.vin ? (
          <Text>
            <strong>VIN:</strong> {vehicle.vin}
          </Text>
        ) : null}
        {trip?.startAt ? (
          <Text>
            <strong>Trip start:</strong> {textOf(trip.startAt)}
          </Text>
        ) : null}
        {trip?.endAt ? (
          <Text>
            <strong>Trip end:</strong> {textOf(trip.endAt)}
          </Text>
        ) : null}
        {placeOf(trip?.pickup) ? (
          <Text>
            <strong>Pickup location:</strong> {placeOf(trip.pickup)}
          </Text>
        ) : null}
        {placeOf(trip?.return) ? (
          <Text>
            <strong>Return location:</strong> {placeOf(trip.return)}
          </Text>
        ) : null}
        {distance?.allowance != null ? (
          <Text>
            <strong>Distance included:</strong> {textOf(distance.allowance)} {textOf(distance.unit)}
          </Text>
        ) : null}
        {money?.total != null ? (
          <Text>
            <strong>Total:</strong> {textOf(money.total)} {textOf(money.currency)}
          </Text>
        ) : null}
        {protection ? (
          <Text>
            <strong>Protection plan:</strong>{' '}
            {textOf(protection.plan || protection.name || protection.summary || 'Recorded')}
          </Text>
        ) : null}
      </Section>

      {lines.length > 0 && (
        <Section id="fee-schedule" data-testid="agreement-fee-schedule">
          <SectionTitle>Fee schedule</SectionTitle>
          {lines.map(line => (
            <Text key={line.key || line.label}>
              <strong>{line.label}:</strong> {line.text}
            </Text>
          ))}
        </Section>
      )}

      <Section id="governing-agreement">
        {versionLine && <Line data-testid="governing-version-line">{versionLine}</Line>}
        {version?.contentMarkdown ? (
          <PolicyMarkdown content={processPolicyContent(version.contentMarkdown)} />
        ) : (
          <Text>The agreement text for this version is not available.</Text>
        )}
      </Section>

      <Section id="acceptance" data-testid="agreement-acceptance">
        <SectionTitle>Acceptance</SectionTitle>
        {acceptances.length === 0 ? (
          <Text>Accepted when this trip was booked.</Text>
        ) : (
          acceptances.map(row => (
            <Text key={row.id}>
              {row.version?.title || 'Document'}
              {row.version?.versionNumber != null
                ? ` version ${row.version.versionNumber}`
                : ''}{' '}
              accepted {row.acceptedAt || ''}
              {row.method ? ` (${row.method})` : ''}
            </Text>
          ))
        )}
      </Section>
    </div>
  );
};
